import { useState } from 'react';

import useSWRInfinite from 'swr/infinite';

import { useClickhouseMediaAnalytics } from '@/utils/hooks/featureFlag/useClickhouseMediaAnalytics';
import { useMediasV2 } from '@/utils/hooks/swr/useMediasV2';

// Utils
import { get } from '@/utils/client';

// API Endpoints
import { GROUP_MEDIAS, MEDIAS } from '@/lib/routes/apiEndpoints';

/**
 * Check if we have at least one valid ID to make an API request
 * @returns {boolean} True if at least one ID is valid
 */
const hasValidID = (groupID, activeSPID, competitorID) => {
    return [groupID, activeSPID, competitorID].some((id) => id != null && id !== undefined);
};

const getKey = (
    pageIndex,
    previousPageData,
    activeSPID,
    limit,
    requestParams,
    endpoint = MEDIAS,
    competitorID,
    isCompetitorRequest,
    groupID
) => {
    // No request params
    if (!requestParams || (!competitorID && isCompetitorRequest)) return null;

    // Validate that we have at least one valid ID to make the request
    if (!hasValidID(groupID, activeSPID, competitorID)) return null;

    // Reached the end
    if (previousPageData && !previousPageData.medias) return null;

    // Determine the endpoint based on whether we're fetching for a group or profile
    let finalEndpoint;
    let finalRequestParams = requestParams;

    if (groupID && endpoint === MEDIAS) {
        finalEndpoint = GROUP_MEDIAS.replace('%GROUP_ID%', groupID);
    } else {
        finalEndpoint = endpoint.replace('%SPID%', competitorID ?? activeSPID);
    }

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
        return [`${finalEndpoint}?per_page=${limit}`, JSON.stringify(finalRequestParams)];
    }

    // Pagination for groups (next_page_url)
    if (previousPageData.next_page_url) {
        return [
            `${finalEndpoint}?per_page=${limit}&page=${previousPageData.current_page + 1}`,
            JSON.stringify(finalRequestParams)
        ];
    }

    // Pagination for profiles (cursor)
    return [
        `${finalEndpoint}?per_page=${limit}&cursor=${previousPageData.cursor}`,
        JSON.stringify(finalRequestParams)
    ];
};

/**
 * Internal hook for v1 media fetching logic.
 * Separated to allow useMedias to delegate to either v1 or v2.
 */
const useMediasV1 = ({
    activeSPID = null,
    groupID = null,
    limit = 20,
    params = null,
    options = {},
    endpoint = undefined,
    isCompetitorRequest = false,
    competitorID = null,
    shouldFetch = true
} = {}) => {
    const [requestParams, setRequestParams] = useState(params);

    const {
        data: dataMedias,
        size,
        setSize,
        mutate: mutateMedias,
        error: mediasError
    } = useSWRInfinite(
        (pageIndex, previousPageData) =>
            shouldFetch
                ? getKey(
                      pageIndex,
                      previousPageData,
                      activeSPID,
                      limit,
                      requestParams,
                      endpoint,
                      competitorID,
                      isCompetitorRequest,
                      groupID
                  )
                : null,
        ([url, params]) => get(url, JSON.parse(params)).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    // Only show loading if shouldFetch is true, we have valid IDs, and are actually fetching
    const isLoadingInitialData =
        shouldFetch && hasValidID(groupID, activeSPID, competitorID) && !dataMedias && !mediasError;
    const isLoadingMore = size > 0 && dataMedias && typeof dataMedias[size - 1] === 'undefined';

    return {
        dataMedias,
        loadMoreMedia: () => setSize(size + 1),
        mutateMedias,
        mediasError,
        requestParams,
        setRequestParams,
        isLoadingInitialData,
        isLoadMore:
            dataMedias &&
            (typeof dataMedias[dataMedias.length - 1]?.cursor === 'string' ||
                typeof dataMedias[dataMedias.length - 1]?.next_page_url === 'string'),
        isLoadingMore
    };
};

const useMedias = ({
    activeSPID = null,
    network = null,
    groupID = null,
    limit = 20,
    params = null,
    options = {},
    endpoint = undefined,
    isCompetitorRequest = false,
    competitorID = null,
    shouldFetch = true
} = {}) => {
    // Check if v2 is active for the given network
    const { isActiveForNetwork } = useClickhouseMediaAnalytics();
    const isMediasV2 = network && isActiveForNetwork(network);

    // Delegate to useMediasV2 when v2 is active
    const v2Result = useMediasV2({
        limit,
        params,
        options,
        shouldFetch: isMediasV2 && shouldFetch
    });

    // Use v1 when v2 is not active
    const v1Result = useMediasV1({
        activeSPID,
        groupID,
        limit,
        params,
        options,
        endpoint,
        isCompetitorRequest,
        competitorID,
        shouldFetch: !isMediasV2 && shouldFetch
    });

    // Return the appropriate result based on which API is active
    const result = isMediasV2 ? v2Result : v1Result;

    return {
        ...result,
        isMediasV2 // Expose whether v2 is being used (useful for components that need to know)
    };
};

export default useMedias;
